import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetAtom, useAtomValue } from 'jotai';
import { supabase } from '@/lib/supabase';
import { authStateAtom } from '@/lib/auth';
import {
  appContextTypeAtom,
  currentCommunityAtom,
  // Import other atoms if they need clearing
} from '@/lib/stores/community';
import { Spinner } from '@/components/ui/atoms/spinner';
import { authLogger } from '@/lib/logger';
import { parseDomain, getSubdomainUrl } from '@/lib/utils/subdomain';

/**
 * AI Context: Logout Page Component
 *
 * Purpose: Handles user logout by calling Supabase signOut, clearing relevant
 *          Jotai state, and redirecting to the appropriate login page based on context.
 * Location: src/pages/auth/Logout.tsx
 * Usage: Navigated to when a user clicks a logout button.
 */
export default function Logout() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(true);

  // Get context and Jotai setters
  const contextType = useAtomValue(appContextTypeAtom);
  const setAuthState = useSetAtom(authStateAtom);
  const setCurrentCommunity = useSetAtom(currentCommunityAtom);
  // Add other setters if needed: setCommunityLoading, setCommunityError, etc.

  useEffect(() => {
    let isMounted = true;
    const handleLogout = async () => {
      setIsLoggingOut(true);
      authLogger?.info({ context: contextType }, 'Logout process started');
      try {
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) throw signOutError;

        authLogger?.info('Supabase signOut successful');

        // Clear relevant Jotai state
        // Note: useAuth hook listener will also clear its state and the main authStateAtom
        // but clearing community context here is good practice.
        setCurrentCommunity(null);
        // Optionally clear other context atoms if necessary

        authLogger?.info('Jotai state cleared, determining redirect');

        // Determine the appropriate login page based on current context
        const { subdomain, type, isCustomDomain } = parseDomain();
        let redirectUrl: string;

        // For custom domains (e.g., hugthebot.com), stay on the current origin
        // Don't use getSubdomainUrl() which would construct hugthebot.com.terrarium.app
        if (isCustomDomain) {
          redirectUrl = `${window.location.origin}/login`;
        } else if (type === 'community' && subdomain) {
          // User is on a community subdomain, redirect to community login
          redirectUrl = getSubdomainUrl(subdomain, '/login');
        } else if (type === 'platform') {
          // User is on platform subdomain, redirect to platform login
          redirectUrl = getSubdomainUrl('platform', '/login');
        } else {
          // User is on main app domain, redirect to main login
          redirectUrl = getSubdomainUrl(null, '/login');
        }

        if (isMounted) {
          authLogger?.info(
            { redirectUrl, context: type, subdomain },
            'Redirecting after logout'
          );
          // Use window.location.href for cross-subdomain redirects
          window.location.href = redirectUrl;
        }
      } catch (err: any) {
        authLogger?.error({ error: err.message }, 'Logout failed');
        if (isMounted) {
          setError(err.message || 'Failed to logout.');
          setIsLoggingOut(false);
        }
      }
      // No finally needed as navigation occurs on success
    };

    handleLogout();

    return () => {
      isMounted = false;
    };
  }, [navigate, contextType, setAuthState, setCurrentCommunity]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center space-y-4">
      {isLoggingOut ? (
        <>
          <Spinner size="lg" />
          <p className="text-gray-600">Logging out...</p>
        </>
      ) : (
        <div className="text-center text-red-600">
          <h1 className="text-xl font-semibold">Logout Failed</h1>
          {error && <p className="mt-2">{error}</p>}
          {/* Optionally add a button to retry or go home */}
        </div>
      )}
    </div>
  );
}
